import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";

// React Router future flags to suppress warnings
const routerFutureFlags = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
};
import { lazy, Suspense, useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { muiTheme } from "@/theme/muiTheme";
import { PWAInstall } from "@/components/PWAInstall";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { LoadingFallback } from "@/components/LoadingFallback";
import { PushNotificationManager } from "@/components/pwa/PushNotificationManager";
import { initPerformanceMonitoring, PerformanceMetricData } from "@/lib/performanceMonitor";
import { supabase } from "@/integrations/supabase/client";
import { routePrefetcher, MOST_VISITED_PAGES } from "@/lib/routePrefetcher";
import { useSyncFavorites } from "@/hooks/useFavorites";
import { initializeAnalytics } from "@/lib/analytics";
import { useAnalytics } from "@/hooks/useAnalytics";
import { ChatWidget } from "@/components/ChatWidget";
import { AdminAutoRedirect } from "@/components/AdminAutoRedirect";
import { LocationProvider } from "@/contexts/LocationContext";
import { AdSenseLoader } from "@/components/ads/AdSenseLoader";
import { AuthModalProvider } from "@/components/auth/AuthModalContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Lazy load all route components for code splitting
const Index = lazy(() => import("./pages/Index"));
const Breeders = lazy(() => import("./pages/Breeders"));
const PetListings = lazy(() => import("./pages/premium-pet-listing"));
const PetDetail = lazy(() => import("./pages/PetDetail"));
const AIExpert = lazy(() => import("./pages/AIExpert"));
const Community = lazy(() => import("./pages/Community"));
const Articles = lazy(() => import("./pages/Articles"));
const Breeds = lazy(() => import("./pages/Breeds"));
const BreedQuiz = lazy(() => import("./pages/BreedQuiz"));
const Calculators = lazy(() => import("./pages/Calculators"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const BreederAuth = lazy(() => import("./pages/BreederAuth"));
const BreederOnboarding = lazy(() => import("./pages/BreederOnboarding"));
const Forum = lazy(() => import("./pages/Forum"));
const QA = lazy(() => import("./pages/QA"));
const Stories = lazy(() => import("./pages/Stories"));
const Events = lazy(() => import("./pages/Events"));
const WebsiteBuilder = lazy(() => import("./pages/WebsiteBuilder"));
const BreederAnalytics = lazy(() => import("./pages/Analytics"));
const PetTracker = lazy(() => import("./pages/PetTracker"));
const BreederPWASettings = lazy(() => import("./pages/BreederPWASettings"));
const Premium = lazy(() => import("./pages/Premium"));
const AISubscription = lazy(() => import("./pages/AISubscription"));
const Messages = lazy(() => import("./pages/Messages"));
const WebsitePreview = lazy(() => import("./pages/WebsitePreview"));
const TestAIService = lazy(() => import("./pages/TestAIService"));
const Favorites = lazy(() => import("./pages/Favorites"));
const TranslationTest = lazy(() => import("./pages/TranslationTest"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminHomepage = lazy(() => import("./pages/AdminHomepage"));
const HomepageAssetManager = lazy(() => import("./pages/HomepageAssetManager"));
const TrainerAuth = lazy(() => import("./pages/TrainerAuth"));
const TrainerDashboard = lazy(() => import("./pages/TrainerDashboard"));
const TrainerSubscription = lazy(() => import("./pages/TrainerSubscription"));
const ClientsPage = lazy(() => import("./pages/trainer/ClientsPage"));
const ClientDetailPage = lazy(() => import("./pages/trainer/ClientDetailPage"));
const SessionsPage = lazy(() => import("./pages/trainer/SessionsPage"));
const Trainers = lazy(() => import("./pages/Trainers"));
const TrainerProfile = lazy(() => import("./pages/TrainerProfile"));
const BreederVerification = lazy(() => import("./pages/admin/BreederVerification"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const PaymentManagement = lazy(() => import("./pages/admin/PaymentManagement"));
const AdminAnalytics = lazy(() => import("./pages/admin/Analytics"));
const OffersManagement = lazy(() => import("./pages/admin/OffersManagement"));
const MarketingDashboard = lazy(() => import("./pages/admin/MarketingDashboard"));
const Integrations = lazy(() => import("./pages/admin/Integrations"));
const TestImageGeneration = lazy(() => import("./pages/TestImageGeneration"));
const ImageGenerator = lazy(() => import("./pages/admin/ImageGenerator"));
const BreederImageGenerator = lazy(() => import("./pages/BreederImageGenerator"));
const PetListingsManagement = lazy(() => import("./pages/admin/PetListingsManagement"));
const BreederContentManagement = lazy(() => import("./pages/admin/BreederContentManagement"));
const BlogManagement = lazy(() => import("./pages/admin/BlogManagement"));
const MediaLibrary = lazy(() => import("./pages/admin/MediaLibrary"));
const RealtimeTest = lazy(() => import("./pages/admin/RealtimeTest"));
const DealsManagement = lazy(() => import("./pages/admin/DealsManagement"));
const CRMIntegrations = lazy(() => import("./pages/admin/CRMIntegrations"));
const TrainerManagement = lazy(() => import("./pages/admin/TrainerManagement"));
const VetManagement = lazy(() => import("./pages/admin/VetManagement"));
const Vets = lazy(() => import("./pages/Vets"));
const VetProfile = lazy(() => import("./pages/VetProfile"));
const VetDashboard = lazy(() => import("./pages/VetDashboard"));
const PetShopManagement = lazy(() => import("./pages/admin/PetShopManagement"));
const PetShops = lazy(() => import("./pages/PetShops"));
const PetShopProfile = lazy(() => import("./pages/PetShopProfile"));
const PetShopDashboard = lazy(() => import("./pages/PetShopDashboard"));
const VoiceInquiry = lazy(() => import("./pages/VoiceInquiry"));
const NotFound = lazy(() => import("./pages/NotFound"));
const HeroBannerManagement = lazy(() => import("./pages/admin/HeroBannerManagement"));
const AdsManagement = lazy(() => import("./pages/admin/AdsManagement"));
const LostFoundManagement = lazy(() => import("./pages/admin/LostFoundManagement"));
const LostAndFound = lazy(() => import("./pages/LostAndFound"));

// Route module loaders for prefetching
const routeModules = {
  '/': () => import("./pages/Index"),
  '/breeders': () => import("./pages/Breeders"),
  '/breeds': () => import("./pages/Breeds"),
  '/breed-quiz': () => import("./pages/BreedQuiz"),
  '/articles': () => import("./pages/Articles"),
  '/calculators': () => import("./pages/Calculators"),
  '/ai-expert': () => import("./pages/AIExpert"),
  '/community': () => import("./pages/Community"),
  '/forum': () => import("./pages/Forum"),
  '/qa': () => import("./pages/QA"),
  '/stories': () => import("./pages/Stories"),
  '/events': () => import("./pages/Events"),
  '/website-builder': () => import("./pages/WebsiteBuilder"),
  '/analytics': () => import("./pages/Analytics"),
  '/pet-tracker': () => import("./pages/PetTracker"),
  '/premium': () => import("./pages/Premium"),
  '/ai-subscription': () => import("./pages/AISubscription"),
  '/subscription': () => import("./pages/AISubscription"),
  '/messages': () => import("./pages/Messages"),
  '/auth': () => import("./pages/Auth"),
  '/dashboard': () => import("./pages/Dashboard"),
  '/breeder-auth': () => import("./pages/BreederAuth"),
  '/breeder-onboarding': () => import("./pages/BreederOnboarding"),
  '/trainer-auth': () => import("./pages/TrainerAuth"),
  '/trainer-dashboard': () => import("./pages/TrainerDashboard"),
  '/trainer-subscription': () => import("./pages/TrainerSubscription"),
  '/admin/homepage': () => import("./pages/AdminHomepage"),
};

// Enhanced React Query configuration with optimized caching strategy
// Requirements: 13.1, 13.4
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes before considering it stale
      staleTime: 5 * 60 * 1000,
      // Keep unused data in cache for 10 minutes (gcTime replaces cacheTime in v5)
      gcTime: 10 * 60 * 1000,
      // Don't refetch on window focus to reduce unnecessary requests
      refetchOnWindowFocus: false,
      // Retry failed requests twice with exponential backoff
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Only refetch if data is stale (not on every mount)
      refetchOnMount: false,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
});

// Analytics wrapper component that must be inside Router
const AnalyticsWrapper = () => {
  useAnalytics();
  return null;
};

const AppContent = () => {
  // Sync local favorites to database when user logs in
  useSyncFavorites();

  useEffect(() => {
    // Initialize 3rd party analytics
    const stored = localStorage.getItem('admin_integrations');
    if (stored) {
      try {
        const config = JSON.parse(stored);
        initializeAnalytics({
          ga4MeasurementId: config.ga4_measurement_id,
          gtmContainerId: config.gtm_container_id,
          fbPixelId: config.fb_pixel_id,
          clarityProjectId: config.clarity_project_id,
          hotjarSiteId: config.hotjar_site_id,
        });
      } catch (error) {
        console.error('Failed to initialize analytics:', error);
      }
    }

    // Initialize performance monitoring on app load
    const initMonitoring = async () => {
      try {
        // Get current user's breeder profile if authenticated
        const { data: { user } } = await supabase.auth.getUser();
        let breederId: string | undefined;

        if (user) {
          const { data: breederProfile, error } = await supabase
            .from('breeder_profiles')
            .select('id')
            .eq('user_id', user.id)
            .single();

          if (!error && breederProfile) {
            breederId = (breederProfile as any).id;
          }
        }

        // Initialize performance monitor with callback to send metrics
        const handleMetric = async (metric: PerformanceMetricData) => {
          // Metrics are automatically sent via the callback
          // Silently collect metrics in development
        };

        initPerformanceMonitoring(breederId, handleMetric);
      } catch (error) {
        console.error('Failed to initialize performance monitoring:', error);
      }
    };

    initMonitoring();

    // Register route modules for prefetching
    routePrefetcher.registerRoutes(routeModules);

    // Prefetch most visited pages after initial load
    // Wait for page to be interactive before prefetching
    if (document.readyState === 'complete') {
      routePrefetcher.prefetchMostVisited(MOST_VISITED_PAGES);
    } else {
      window.addEventListener('load', () => {
        routePrefetcher.prefetchMostVisited(MOST_VISITED_PAGES);
      });
    }
  }, []);

  return (
    <>
      <Toaster />
      <Sonner />
      <PWAInstall />
      <PushNotificationManager />
      <OfflineIndicator />
      <ChatWidget />
      <BrowserRouter future={routerFutureFlags}>
        <AuthModalProvider>
          <AdminAutoRedirect />
          <AnalyticsWrapper />
          <AdSenseLoader />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/pets" element={<PetListings />} />
            <Route path="/pet-listings" element={<PetListings />} />
            <Route path="/breeds" element={<Breeds />} />
            <Route path="/breed-quiz" element={<BreedQuiz />} />
            <Route path="/breeders" element={<Breeders />} />

            <Route path="/articles" element={<Articles />} />
            <Route path="/calculators" element={<Calculators />} />
            <Route path="/ai-expert" element={<AIExpert />} />
            <Route path="/community" element={<Community />} />
            <Route path="/forum" element={<Forum />} />
            <Route path="/qa" element={<QA />} />
            <Route path="/stories" element={<Stories />} />
            <Route path="/events" element={<Events />} />
            <Route path="/lost-and-found" element={<LostAndFound />} />
            <Route path="/website-builder" element={<WebsiteBuilder />} />
            <Route path="/analytics" element={<BreederAnalytics />} />
            <Route path="/pet-tracker" element={<PetTracker />} />
            <Route path="/breeder-pwa-settings" element={<BreederPWASettings />} />
            <Route path="/premium" element={<Premium />} />
            <Route path="/ai-subscription" element={<AISubscription />} />
            <Route path="/subscription" element={<AISubscription />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/voice-inquiry" element={<VoiceInquiry />} />
            <Route path="/favorites" element={<Favorites />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/breeder-auth" element={<BreederAuth />} />
            <Route path="/breeder-onboarding" element={<BreederOnboarding />} />
            <Route path="/trainer-auth" element={<TrainerAuth />} />
            <Route path="/trainer-dashboard" element={<TrainerDashboard />} />
            <Route path="/trainer-subscription" element={<TrainerSubscription />} />
            <Route path="/trainer/clients" element={<ClientsPage />} />
            <Route path="/trainer/clients/:clientId" element={<ClientDetailPage />} />
            <Route path="/trainer/sessions" element={<SessionsPage />} />
            <Route path="/trainers" element={<Trainers />} />

            <Route path="/pets/:id" element={<PetDetail />} />
            <Route path="/test-ai" element={<TestAIService />} />
            <Route path="/test-translation" element={<TranslationTest />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/users" element={<UserManagement />} />
            <Route path="/admin/payments" element={<PaymentManagement />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/pet-listings" element={<PetListingsManagement />} />
            <Route path="/admin/breeder-content" element={<BreederContentManagement />} />
            <Route path="/admin/blog" element={<BlogManagement />} />
            <Route path="/admin/media" element={<MediaLibrary />} />
            <Route path="/admin/offers" element={<OffersManagement />} />
            <Route path="/admin/breeder-verification" element={<BreederVerification />} />
            <Route path="/admin/marketing" element={<MarketingDashboard />} />
            <Route path="/admin/integrations" element={<Integrations />} />
            <Route path="/admin/image-generator" element={<ImageGenerator />} />
            <Route path="/admin/deals" element={<DealsManagement />} />
            <Route path="/admin/crm-integrations" element={<CRMIntegrations />} />
            <Route path="/admin/trainers" element={<TrainerManagement />} />
            <Route path="/admin/vets" element={<VetManagement />} />
            <Route path="/vets" element={<Vets />} />

            <Route path="/vet-dashboard" element={<VetDashboard />} />
            <Route path="/admin/pet-shops" element={<PetShopManagement />} />
            <Route path="/admin/lost-found" element={<LostFoundManagement />} />
            <Route path="/pet-shops" element={<PetShops />} />

            <Route path="/pet-shop-dashboard" element={<PetShopDashboard />} />
            <Route path="/admin/realtime-test" element={<RealtimeTest />} />
            <Route path="/admin/users" element={<UserManagement />} />
            <Route path="/admin/hero-banners" element={<HeroBannerManagement />} />
            <Route path="/admin/ads" element={<AdsManagement />} />
            <Route path="/breeder/image-generator" element={<BreederImageGenerator />} />
            <Route path="/test-image-generation" element={<TestImageGeneration />} />
            <Route path="/vet-profile/:id" element={<VetProfile />} />
            <Route path="/trainer-profile/:id" element={<TrainerProfile />} />
            <Route path="/pet-shop-profile/:id" element={<PetShopProfile />} />

            <Route path="/breeder/:id" element={<WebsitePreview />} />
            <Route path="/pet-shop/:id" element={<WebsitePreview />} />
            <Route path="/trainer/:id" element={<WebsitePreview />} />
            <Route path="/vet/:id" element={<WebsitePreview />} />
            <Route path="/preview/:id" element={<WebsitePreview />} />
            {/* Custom slug route for websites - must be before NotFound */}
            <Route path="/:slug" element={<WebsitePreview />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthModalProvider>
      </BrowserRouter>
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={muiTheme}>
          <CssBaseline />
          <TooltipProvider>
            <LocationProvider>
              <AppContent />
            </LocationProvider>
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
